import * as R from 'ramda';
import { Dispatch } from 'redux';
import { copyToClipboardAction } from '../../copyToClipboard/copyToClipboardAction';
import { IKON_MODAL } from '../../core/modal/ikonModal';
import { showModalAction } from '../../core/modal/showModalAction';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { ApiError } from '../../core/redux/api/apiError';
import { getError, getLoading } from '../../core/redux/api/module';
import { IAppState } from '../../core/redux/IAppState';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import ImmutableMap from '../../language/ImmutableMap';
import { getGlobalAndLocalResortList } from '../../resortDetails/module';
import { Resort } from '../../resortDetails/resort';
import { getEntitlementHotlistAction } from '../entitlements/guestEntitlementHotlistAction';
import { guestEntitlementsAction } from '../entitlements/guestEntitlementsAction';
import { EntitlementHotlist, QuickEntitlement } from '../entitlements/models';
import { getActivePassEntitlements, getExpiredPassEntitlements, getGuestHotlists, isActive, isDeferred } from '../entitlements/module';
import { getHasCommentsAction } from '../guestComments/getHasCommentsAction';
import { GuestDetails } from '../guestDetails';
import { getGuestPhotoAction } from '../guestPhoto/getGuestPhotoAction';
import {
  hasAnyFutureActiveGlobalHotlists,
  hasAnyFutureActiveHotlistsForResort,
  hasNoFutureActiveGlobalHotlists,
  hasNoFutureActiveHotlistsForResort,
} from '../hotlist/module';
import { importGuestAction } from '../importGuest/importGuestAction';
import { getImportGuestSuccess } from '../importGuest/module';
import { getDisabledMediaBannerTextForGuest, getHotlistBannerTextForGuest } from './module';
import { getGuestMediaHistory } from '../entitlements/media/mediaHistory/module';
import { getMediaHistoryAction } from '../entitlements/media/mediaHistory/getMediaHistoryAction';
import { HistoricalMedia } from '../entitlements/media/mediaHistory/historicalMedia';
import { getResource } from '../../language/resources/getResource';
import { getGuestPaymentRegistrationsAction } from '../guestPayment/getGuestPaymentRegistrationAction';
import { SerializedStyles } from '@emotion/react';
import { getPaymentMethodAction } from '../guestPayment/getPaymentMethodAction';
import { GuestPaymentRegistration } from '../guestPayment/paymentRegistration';


export type GuestCardDispatchProps = {
  showModal: (modalType: IKON_MODAL, extras?: any) => void;
  getEntitlements: (guest: GuestDetails) => void;
  getPhoto: (guest: GuestDetails, cloudinaryUrl: string) => void;
  getHasComments: (guestId: string) => void;
  getEntitlementHotlists: (entitlement: QuickEntitlement, resortList: Resort[] | undefined) => void;
  getMediaHistory?: (guestId: string) => void;
  getPaymentRegistrations: (guest: GuestDetails) => void;
  getPaymentMethod: (guest: GuestDetails) => void;

  importGuest: (guest: GuestDetails) => void;

  copyGuestIdToClipboard: (guestId: string) => void;
  copyMediaIdToClipboard: (mediaId: string) => void;
};

export type GuestCardStateProps = {
  // Successful State
  importGuestSuccess: boolean;
  isPrinted: boolean;
  entitlementHotlists: ImmutableMap<EntitlementHotlist[]>;

  // Derived state information
  hasHotlistedPass: boolean;
  hasExpiredHotlistedPass: boolean;
  hasHotlistHistory: boolean;
  hasHotlistablePass: boolean;
  hotlistBannerMessage: string | undefined;
  deferredBannerMessage: string | undefined;
  mediaHistory?: HistoricalMedia[];
  disabledMediaBannerMessage?: string;

  // Loading
  loadingEntitlements: boolean;
  loadingPhoto: boolean;
  loadingRfid: boolean;
  loadingWaiver: boolean;
  loadingImportGuest: boolean;
  loadingHasComments: boolean;
  loadingEntitlementHotlists: boolean;
  loadingPaymentRegistrations: boolean;
  loadingPaymentMethod: boolean

  // Error
  errorSubmitLocalRef: ApiError | undefined;

  // Auth
  enableDisableMedia: boolean;
  enableAddToHotlist: boolean;
  enableRemoveFromHotlist: boolean;
  enableCustomerImport: boolean;
  enableGlobalHotlist: boolean;
  enableResortSpecificHotlist: boolean;
  passEntitlementId: number;
  showPaymentRegistrations: boolean;

  // Resort info
  resortList: Resort[];

  // Resources
  resources: Record<string, any>;
  dateFormat: string;
}


export type  GuestCardProps = {
  guest: GuestDetails | undefined;
  entitlementTypes: ImmutableMap<EntitlementType> | undefined;
  loadingGuestDetails: boolean;
  guestDetailsError: string | undefined;
  rootCss?: SerializedStyles;
  resources: Record<string, any>;
};

type Props = GuestCardStateProps & GuestCardDispatchProps & GuestCardProps;

export type GuestCardState = {
  /**
   * Whether or not we should be showing the open overflow menu inside the GuestCard.
   */
  overflowMenuIsOpen: boolean;

  /**
   * The current modal that we should be showing for the GuestCard.
   */
  currentModal: IKON_MODAL;

  /**
   * Has the component attempted to load media history
   */
  hasLoadedMediaHistory: boolean;
};


export const INITIAL_GUEST_CARD_STATE: GuestCardState = {
  overflowMenuIsOpen: false,
  currentModal:       IKON_MODAL.NONE,
  hasLoadedMediaHistory: false,
};



/**
 * Function determining when Redux should tell GuestCard of state changes (or when it shouldn't). For performance reasons
 * we don't want a million unrelated Redux updates causing GuestCard to rerender.
 */
export const statesAreEqual = (
  next: IAppState,
  prev: IAppState,
): boolean => next.loading === prev.loading
              && next.error === prev.error
              && next.guests === prev.guests
              && next.passPrinting.printed === prev.passPrinting.printed
              && next.importSuccesses === prev.importSuccesses
              && next.auth === prev.auth;


/**
 * The GuestCard's map state to props function that takes the current app state, and uses it to populate the GuestCard
 * component's props. Props are the component's inputs, and so this function will be run every time app state changes.
 * This function should return a new props object for the GuestCard to use.
 * @param state - The current app state
 * @param props - The component's current props
 */
export const mapStateToProps = (state: IAppState, props: GuestCardProps): GuestCardStateProps => {
  const guestId               = props.guest?.masterGuestId;
  const entitlementIds        = R.map(quickEnt => `${quickEnt.entitlementId}`, props.guest?.entitlements || []);
  const guest                 = guestId ? state.guests.get(guestId) : undefined;
  const guestPassEntitlements = getActivePassEntitlements(state.resortToolsConfig.dtwCategoryId, guest);
  const expiredPassEntitlements = getExpiredPassEntitlements(state.resortToolsConfig.dtwCategoryId, guest);

  const isLoadingEntitlement = actionType => R.isEmpty(entitlementIds)
    ? false
    : R.any(entId => !!getLoading(state, entId, actionType), entitlementIds);

  const isLoading = actionType => guestId ? !!getLoading(state, guestId, actionType) : false;
  const errorFor  = actionType => guestId ? getError(state, guestId, actionType) : undefined;

  const currentResort       = state.selectedResort;
  const currentResortAbbrev = currentResort?.resortAbbreviation || '';

  const resortList   = getGlobalAndLocalResortList(state);
  const hotlists     = getGuestHotlists(state);
  const mediaHistory = getGuestMediaHistory(state, guestId);

  return {
    // Loading
    loadingEntitlements:          isLoading(ACTION_TYPE.GUEST_ENTITLEMENTS_REQUEST),
    loadingPhoto:                 isLoading(ACTION_TYPE.GET_GUEST_PHOTO_REQUEST)
                                  || isLoading(ACTION_TYPE.SET_GUEST_PHOTO_REQUEST),
    loadingRfid:                  isLoading(ACTION_TYPE.ASSOC_RFID_REQUEST),
    loadingWaiver:                isLoading(ACTION_TYPE.SUBMIT_WAIVER_REQUEST),
    loadingImportGuest:           isLoading(ACTION_TYPE.IMPORT_GUEST_REQUEST),
    loadingHasComments:           isLoading(ACTION_TYPE.GET_HAS_COMMENTS_REQUEST),
    loadingEntitlementHotlists:   isLoadingEntitlement(ACTION_TYPE.GET_ENTITLEMENT_HOTLIST_REQUEST),
    loadingPaymentRegistrations:  isLoading(ACTION_TYPE.GET_GUEST_PAYMENT_REGISTRATION_REQUEST),
    loadingPaymentMethod:         isLoading(ACTION_TYPE.GET_PAYMENT_METHOD_REQUEST),

    // Error
    errorSubmitLocalRef: errorFor(ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST),

    // Successful State
    importGuestSuccess: getImportGuestSuccess(state, guestId),
    isPrinted:          R.includes(guestId, state.passPrinting.printed),

    // Auth
    enableDisableMedia:      R.includes(state.userState.role, state.resortToolsConfig.enableDisableMedia),
    enableAddToHotlist:      R.includes(state.userState.role, state.resortToolsConfig.enableAddToHotlist),
    enableRemoveFromHotlist: R.includes(state.userState.role, state.resortToolsConfig.enableRemoveFromHotlist),
    enableCustomerImport:    R.includes(state.userState.role, state.resortToolsConfig.enableCustomerImport),
    enableGlobalHotlist:         R.path(['enableGlobalHotlist'], state.resortToolsConfig),
    enableResortSpecificHotlist: R.path(['enableResortSpecificHotlist'], state.resortToolsConfig),
    showPaymentRegistrations: state.resortToolsConfig.enablePowCoin,

    passEntitlementId:       state.resortToolsConfig.dtwCategoryId,

    // Resort info
    // Remove blank entry if home resort missing
    resortList: resortList,

    // Deferred Info
    deferredBannerMessage:
      R.any(entitlement => isDeferred(entitlement) && isActive(entitlement), guest?.entitlements || [])
        ? getResource(state.locale, 'GUEST_CARD_DEFERRED_BANNER') : undefined,

    // Hotlist info
    entitlementHotlists: hotlists,

    // True if guest has any entitlements with any hotlist history
    hasHotlistHistory:   R.any(
      entitlementId => {
        const entHotlists = hotlists.get(String(entitlementId)) || [];
        return entHotlists.length > 0;
      },
      entitlementIds,
    ),

    /*
     True if guest has any entitlements and any of those entitlements are:
     (1) not hotlisted globally AND
     (2) not hotlisted for the current resort
     (since we can hotlist for this resort even if already globally hotlisted)
     */
    hasHotlistablePass: R.any(
      entitlement => {
        const entHotlists = hotlists.get(String(entitlement.entitlementId)) || [];
        return hasNoFutureActiveGlobalHotlists(entHotlists)
               || hasNoFutureActiveHotlistsForResort(entHotlists, currentResortAbbrev);
      },
      guestPassEntitlements,
    ),

    // True if any entitlements have any active or future active hotlists
    hasHotlistedPass: R.any(
      entitlement => {
        const entHotlists = hotlists.get(String(entitlement.entitlementId)) || [];
        return hasAnyFutureActiveGlobalHotlists(entHotlists)
               || hasAnyFutureActiveHotlistsForResort(entHotlists, currentResortAbbrev);
      },
      guestPassEntitlements,
    ),

    hasExpiredHotlistedPass: R.any(
      entitlement => {
        const entHotlists = hotlists.get(String(entitlement.entitlementId)) || [];
        return hasAnyFutureActiveGlobalHotlists(entHotlists)
               || hasAnyFutureActiveHotlistsForResort(entHotlists, currentResortAbbrev);
      },
      expiredPassEntitlements,
    ), 

    hotlistBannerMessage: getHotlistBannerTextForGuest(state.locale, guest, hotlists, state.resortDetails!.allResorts),

    mediaHistory,
    disabledMediaBannerMessage: getDisabledMediaBannerTextForGuest(state.locale, guest, mediaHistory),

    resources: state.locale,
    dateFormat: state.defaultDateFormat,
  };
};


/**
 * The GuestCard's map dispatch to props function that takes the app store's dispatch function and uses it to make
 * action handlers. Should return new props object for the component to use.
 * @param dispatch: The Redux dispatch function that sends actions to the store for reduction into app state
 */
export const mapDispatchToProps = (dispatch: Dispatch, ownProps: Props): GuestCardDispatchProps => ({
  showModal:   (modalType: IKON_MODAL, extras?: any) => dispatch(showModalAction(modalType, extras)),
  importGuest: (guest: GuestDetails) => dispatch(importGuestAction(guest)),

  // TODO: We should wrap these three (+ getGuestDetails) into a single prop and call it from household
  getEntitlements:        (guest: GuestDetails) => dispatch(guestEntitlementsAction(guest)),
  getEntitlementHotlists: (
                            entitlement: QuickEntitlement,
                            resortList: Resort[],
                          ) => dispatch(getEntitlementHotlistAction(entitlement, resortList)),

  getMediaHistory:          (guestId: string) => dispatch(getMediaHistoryAction(guestId)),
  getHasComments:           (guestId: string) => dispatch(getHasCommentsAction(guestId)),
  getPaymentRegistrations:  (guest: GuestDetails) => dispatch(getGuestPaymentRegistrationsAction(guest)),
  getPaymentMethod: (guest: GuestDetails) => dispatch(getPaymentMethodAction(getPaymentRegistrationWithMaxExpirationDate(guest.paymentRegistrations), guest.masterGuestId)),

  getPhoto: (guest: GuestDetails, cloudinaryUrl) => dispatch(getGuestPhotoAction(guest, cloudinaryUrl, 'small')),

  copyGuestIdToClipboard: (guestId: string) => dispatch(
    copyToClipboardAction(guestId, getResource(ownProps.resources, 'GUEST_CARD_COPIED_MASTER_GUEST_ID')),
  ),
  copyMediaIdToClipboard: (mediaId: string) => dispatch(
    copyToClipboardAction(mediaId, getResource(ownProps.resources, 'GUEST_CARD_COPIED_MEDIA_ID')),
  ),
});

function getPaymentRegistrationWithMaxExpirationDate(paymentRegistrations: GuestPaymentRegistration[] | undefined) {
      return paymentRegistrations!.reduce((max, registration) => (new Date(registration.expirationDate) > new Date(max.expirationDate) ? registration : max));

}

